<script setup lang="ts">
import CompletedProgress from '@/vue/atoms/completed-progress.vue';
import {getVueLink, useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import {computed} from 'vue';
import BackLink from '@/vue/molecules/back-link.vue';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {pagesPath} from '@/ts/router/pages-path';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useI18n} from 'vue-i18n';

const intervention = useInterventionStore();
const {iid, sid} = useRouterSectionParams();
const {session, order} = useSessionId(sid);
const {t: $t} = useI18n();

const interventionLandingLink = getVueLink(pagesPath.intervention.interventionLanding, {iid: iid.value});

// Create a display list with all pages marked as completed + session completed item
const displayedItems = computed<{ name: string; index: number; isSessionCompleted: boolean }[]>(() => {
  const allPages = session.value?.pages ?? [];
  const totalPages = allPages.length;

  // Regular pages (all marked as completed, no interactions)
  const pageItems = allPages.map((page, i) => ({
    name: page.name || $t('page_n', {n: i + 1}),
    index: i + 1,
    isSessionCompleted: false,
  }));

  // Session completed item (always present and styled differently)
  const sessionCompletedItem = {
    name: 'Session Completed',
    index: totalPages + 1,
    isSessionCompleted: true,
  };

  return [...pageItems, sessionCompletedItem];
});

// For completed sessions, always show 100% - use 1.0 since component multiplies by 100
const completedPercentOverride = computed(() => 1.0);
</script>

<template>
  <div>
    <back-link :label="intervention!.name" :to="interventionLandingLink!"/>
    <div class="session-index">
      {{ $t('session_index', {order}) }}
    </div>
    <completed-progress class="progress" :completed="completedPercentOverride"/>

    <nav class="nav-boundary" :aria-label="$t('session_nav')">
      <ol class="nav-session-pages">
        <li v-for="item in displayedItems" :key="`item-${item.index}`" class="session-toc-item">
          <div
            class="link"
            :class="item.isSessionCompleted ? 'completed-highlighted' : 'completed'"
            role="text"
          >
            <div class="label">
              <span class="text-body-sm">
                {{ item.isSessionCompleted ? $t('end') : $t('page_n', {n: item.index}) }}
              </span>
              <span class="text-body-sm-bold">
                {{ item.isSessionCompleted ? $t('session_completed') : item.name }}
              </span>
            </div>
            <div class="icon">
              <svg-illustration name="check.svg"/>
            </div>
          </div>
        </li>
      </ol>
    </nav>
  </div>
</template>

<style scoped lang="scss">
.nav-session-pages {
  @include dashed-between-elements(var(--Spacing-spacing-2));
  width: 100%;
  margin: 0;
  padding: 0;
}

.nav-boundary {
  margin: var(--Spacing-spacing-5) -5px 0 -5px;
  padding: 5px; // give room for outline of :focus-visible (keyboard usage)
}

.session-index {
  @include font-body;
  justify-self: flex-end;
}

.progress {
  @include font-body-bold;
}

.session-toc-item {
  list-style: none;
  position: relative;
}

.link {
  width: 100%;
  display: flex;
  padding: var(--Spacing-spacing-3);
  position: relative;
  gap: var(--Spacing-spacing-2);
  flex-direction: row;
}

.current {
  @include q-card-blue;
}

.completed {
  @include q-card-dark-completed;
}

.completed-highlighted {
  @include q-card-dark-completed;
}

.label {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
  align-items: flex-start;
  text-align: left;
}

.icon {
  align-self: end;
  margin-left: auto;
  position: relative;
  top: 1px;
  flex-shrink: 0;
}
</style>